import { formatCurrency } from '../lib/format.js';

const QUICK_RATES = [20, 10, 5.5, 2.1];

function money(cents) {
  return cents == null ? '—' : formatCurrency(Math.abs(cents));
}

export function extractedVatState(ex) {
  if ((ex?.vat_breakdown?.length ?? 0) > 1) {
    return {
      mode: 'multi',
      lines: ex.vat_breakdown.map((b) => ({ rate: b.rate ?? null, amountCents: b.vat_amount_cents ?? null })),
    };
  }
  return { mode: 'single', rate: ex?.vat_rate ?? null, amountCents: ex?.vat_amount_cents ?? null };
}

// Même sémantique que la revue des justificatifs en lot : multi-taux → total sans taux + détail si complet.
export function vatPayloadFromState(vat) {
  if (vat.mode === 'single') return { vat_rate: vat.rate, vat_amount_cents: vat.amountCents };
  const complete = vat.lines.filter((l) => l.rate != null && l.amountCents != null);
  const distinct = new Set(complete.map((l) => l.rate)).size === complete.length;
  return {
    vat_rate: null,
    vat_amount_cents: vat.lines.some((l) => l.amountCents != null)
      ? vat.lines.reduce((s, l) => s + (l.amountCents ?? 0), 0)
      : null,
    ...(complete.length >= 2 && distinct
      ? { vat_breakdown: complete.map((l) => ({ rate: l.rate, vat_amount_cents: Math.abs(l.amountCents) })) }
      : {}),
  };
}

const inputCls = 'min-h-11 w-24 rounded-lg border border-slate-300 px-2 text-base tabular-nums text-slate-700';

export default function ReceiptVatFields({ vat, totalCents, onChange }) {
  function applyRate(rate) {
    onChange({
      mode: 'single',
      rate,
      amountCents: totalCents != null ? Math.round((totalCents * rate) / (100 + rate)) : vat.amountCents ?? null,
    });
  }

  if (vat.mode === 'multi') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500">TVA appliquée (multi-taux)</span>
          <button
            type="button"
            onClick={() => onChange({ mode: 'single', rate: null, amountCents: null })}
            className="min-h-11 px-2 text-xs text-slate-500 underline"
          >
            Effacer
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          {vat.lines.map((line, i) => {
            const label = line.rate != null ? `${line.rate.toLocaleString('fr-FR')} %` : 'sans taux';
            return (
              <label key={i} className="inline-flex items-center gap-2 text-sm text-slate-600">
                <span className="tabular-nums">{label}</span>
                <input
                  aria-label={`Montant TVA ${label} (€)`}
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  value={line.amountCents != null ? line.amountCents / 100 : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const amountCents = v === '' ? null : Math.round(parseFloat(v) * 100);
                    onChange({ mode: 'multi', lines: vat.lines.map((l, j) => (j === i ? { ...l, amountCents } : l)) });
                  }}
                  className={inputCls}
                />
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500">TVA appliquée</span>
        {(vat.rate != null || vat.amountCents != null) && (
          <button
            type="button"
            aria-label="Effacer la TVA"
            onClick={() => onChange({ mode: 'single', rate: null, amountCents: null })}
            className="min-h-11 px-2 text-xs text-slate-500 underline"
          >
            Effacer
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {QUICK_RATES.map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={vat.rate === r}
            onClick={() => applyRate(r)}
            className={`min-h-11 rounded-full px-3 text-sm ring-1 ${
              vat.rate === r ? 'bg-slate-800 text-white ring-slate-800' : 'bg-white text-slate-700 ring-slate-300'
            }`}
          >
            {r.toLocaleString('fr-FR')} %
          </button>
        ))}
        <input
          aria-label="Montant TVA (€)"
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          value={vat.amountCents != null ? vat.amountCents / 100 : ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange({ mode: 'single', rate: vat.rate, amountCents: v === '' ? null : Math.round(parseFloat(v) * 100) });
          }}
          className={inputCls}
        />
      </div>
      {vat.amountCents != null && <p className="text-xs text-slate-400">TVA {money(vat.amountCents)}</p>}
    </div>
  );
}
